import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  alpha,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  IconProps,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from "@mui/icons-material";
import { FormatDate, isDateString } from "~/lib/date";
import { cloneElement, ReactElement, useEffect, useState } from "react";
import { Asset, InitiativeStatus } from "~/operations";
import { TriageBarActionButtons } from "~/components/TriageBar/TriageBarActionButtons";
import { TakeActionButton } from "~/components/TakeAction/TakeActionButton";
import { setDocumentTitle } from "~/lib/commonUtils";
import { TriageBarSkeleton } from "./TriageBarSkeleton";
import CenterEllipseText from "../CenterEllipseText/CenterEllipseText";
import { useScreenSize } from "~/hooks/DevTools";
import { FindingSubheader } from "~/pages/findings/FindingOnAsset/utils";
import { ConversationProperties } from "~/hooks/useConversationStarter/types";
import { InitiativeStatusChip } from "~/pages/space/home-dashboard/TopActionsCard/components/InitiativeStatusChip";

export type TriageBarProps = {
  title: string;
  titleIcon?: ReactElement<IconProps>;
  fallbackPath?: string;
  subheaders?: FindingSubheader[];
  toolbarItems?: { id: string; component: ReactElement }[];
  additionalMenuItems?: React.ReactNode;
  triage?: boolean; // Optional prop to show the triage button
  asset?: Asset | undefined | null;
  isLoading: boolean;
  showBackButton?: boolean; // Optional prop to show/hide the back button (default: true)
  compact?: boolean; // Optional prop to make subheaders compact with comma separation (default: false)
  availableRemediations?: {
    hasRemediations?: boolean;
    ticket: {
      active: boolean;
      onClick?: () => void;
      disabled?: boolean;
    };
    exception: {
      active: boolean;
      onClick?: () => void;
      disabled?: boolean;
      hasException?: boolean;
      linkToException?: string;
      shouldNavigateToSpaceLevel?: boolean;
    };
    securityPipeline: {
      active: boolean;
      onClick?: () => void;
      disabled?: boolean;
    };
  };
  conversation?: ConversationProperties;
  initiativeStatus?: InitiativeStatus;
};

const sharedTransitions = {
  transitionDuration: "0.35s",
  transitionTimingFunction: "ease-in-out",
  transformOrigin: "bottom left",
};

export function TriageBar({
  title,
  titleIcon,
  fallbackPath,
  subheaders,
  toolbarItems,
  additionalMenuItems,
  triage = false,
  asset,
  isLoading,
  showBackButton = true,
  compact = false,
  availableRemediations,
  conversation,
  initiativeStatus,
}: TriageBarProps) {
  if (isLoading) {
    return <TriageBarSkeleton />;
  }

  const navigate = useNavigate();
  const location = useLocation();

  const [shrink, setShrink] = useState(false);
  const { size } = useScreenSize();
  const { exception } = availableRemediations || {};

  const showInitiativeStatus =
    initiativeStatus && initiativeStatus !== InitiativeStatus.NotStarted;
  const showTriageButton = triage && !showInitiativeStatus;

  const handleScroll = () => {
    setShrink(window.scrollY > 5);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const onBackButtonClick = () => {
    const isPreviousRouteExist = location.key !== "default";
    const isInitiativeDetailPage = location.pathname.includes("/initiatives/");

    if (isPreviousRouteExist && !isInitiativeDetailPage) {
      navigate(-1);
      return;
    }

    if (fallbackPath) {
      navigate(fallbackPath);
    }
  };

  const triageBarTitle = asset ? `${title} on ${asset.name}` : title;

  setDocumentTitle(triageBarTitle);

  return (
    <Grid
      data-testid="triage-bar"
      container
      rowSpacing={2}
      columnSpacing={3}
      sx={{
        position: "sticky",
        top: shrink ? (title.length > 1 ? 60 : 0) : 64,
        transitionProperty: "top top, background-color",
        ...sharedTransitions,
        transformOrigin: "top",
        background: (theme) =>
          shrink
            ? theme.palette.background.paper
            : theme.palette.background.default,
        zIndex: 99,
      }}
    >
      <Grid
        container
        size={12}
        sx={{
          width: 1,
          mx: "auto",
          py: 3,
          px: { xs: 2, sm: 3, md: 4 },
          flexWrap: "nowrap",
        }}
      >
        <Grid
          container
          rowSpacing={1}
          flexDirection="column"
          justifyContent="space-between"
          size={{ xs: 12, sm: 12, md: "grow" }}
          marginTop={shrink && !asset?.name ? "15px" : 0}
        >
          <Grid size={12}>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              flexGrow={1}
              flexShrink={1}
              minWidth={0}
              sx={{
                ...sharedTransitions,
                transformProperty: "transform",
              }}
            >
              <Stack direction="row" alignItems="center" flex={1} minWidth={0}>
                {showBackButton && (
                  <IconButton
                    onClick={onBackButtonClick}
                    aria-label="Go back"
                    sx={{
                      color: "primary.main",
                      p: 0,
                      mr: 0.5,
                    }}
                  >
                    <ChevronLeftIcon fontSize="large" />
                  </IconButton>
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  minWidth={0}
                  width="100%"
                >
                  {titleIcon &&
                    cloneElement(titleIcon, { fontSize: "inherit" })}
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      pl: 1,
                    }}
                  >
                    <CenterEllipseText
                      id="triage-bar-title"
                      titles={[title, asset?.name].filter(Boolean) as string[]}
                      TextStyle={{
                        fontSize: size.isSmallerThanLg ? "24px" : "32px",
                        fontWeight: 900,
                      }}
                      shrink={shrink}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Grid>

          <Grid
            sx={{
              ".info-block": {
                ...sharedTransitions,
                transitionProperty: "transform translate",
              },
              ".info-asset-title": { fontWeight: 700 },
              ".info-title": {
                fontWeight: 700,
                color: (theme) => alpha(theme.palette.text.primary, 1),
              },
              ml: 1,
              span: { fontWeight: 400, color: "inherit" },
            }}
          >
            {compact ? (
              <Typography variant="subtitle2" className="info-block">
                {subheaders?.map((subheader, index) => {
                  if (subheader.component) {
                    return cloneElement(subheader.component, {
                      key: subheader.id,
                      id: subheader.id,
                    });
                  }
                  const isDateValue = isDateString(subheader.value);
                  const value = isDateValue
                    ? FormatDate(subheader.value)
                    : subheader.value;
                  return (
                    <Typography
                      key={subheader.id}
                      component="span"
                      variant="subtitle2"
                    >
                      {subheader.label}{" "}
                      <Typography
                        component="span"
                        variant="subtitle2"
                        className="info-title"
                      >
                        {value}
                      </Typography>
                      {index < (subheaders?.length || 0) - 1 && ", "}
                    </Typography>
                  );
                })}
              </Typography>
            ) : (
              <Stack
                direction={{ xs: "column", md: "row" }}
                columnGap={3}
                rowGap={0.5}
                className="info-block"
              >
                {/* Subheaders */}
                {subheaders?.map((subheader) => {
                  if (subheader.component) {
                    return cloneElement(subheader.component, {
                      key: subheader.id,
                      id: subheader.id,
                    });
                  }
                  return (
                    <Typography
                      key={subheader.id}
                      id={subheader.id}
                      variant="subtitle2"
                      className="info-title"
                    >
                      {subheader.label}{" "}
                      <Typography component="span" variant="subtitle2">
                        {isDateString(subheader.value)
                          ? FormatDate(subheader.value)
                          : subheader.value}
                      </Typography>
                    </Typography>
                  );
                })}
              </Stack>
            )}
          </Grid>
        </Grid>

        {/* No Exceptions or Tickets on CVEs yet */}
        <Grid
          container
          rowSpacing={1}
          flexDirection="column"
          size={{ xs: 6, md: "auto" }}
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <Grid
            container
            sx={{
              flexWrap: "nowrap",
              alignItems: "center",
            }}
          >
            <Grid size={{ xs: "grow" }}>
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                justifyContent={{ xs: "flex-end" }}
                sx={{
                  ...sharedTransitions,
                  transitionProperty: "transform translate",
                }}
              >
                {toolbarItems?.map((item, index) =>
                  cloneElement(item.component, {
                    key: index,
                  }),
                )}
                <TriageBarActionButtons
                  IconButtonProps={{ sx: { color: "text.primary" } }}
                  title={title}
                  additionalMenuItems={additionalMenuItems}
                />
                {showTriageButton && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: "none", md: "flex" }, mx: 1 }}
                  />
                )}
              </Stack>
            </Grid>
            {/* Triage Button */}
            {showTriageButton && (
              <Grid
                size={{ xs: "auto" }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  pl: 1,
                  ...(shrink && {
                    ...sharedTransitions,
                    transformOrigin: "bottom right",
                  }),
                }}
              >
                <TakeActionButton
                  {...{
                    availableRemediations,
                    conversation,
                    type: "triage-bar",
                  }}
                />
              </Grid>
            )}
            {/* Initiative Status */}
            {showInitiativeStatus && initiativeStatus && (
              <InitiativeStatusChip status={initiativeStatus} />
            )}
          </Grid>
          {/* no slack integration yet */}
          {exception?.hasException && (
            <ExceptionStatusIndicator
              {...{ linkToException: exception.linkToException }}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

const ExceptionStatusIndicator = ({
  linkToException,
}: {
  linkToException?: string;
}) => {
  return (
    <Grid>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={3}
        sx={{ ml: "auto", maxWidth: 256 }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: (theme) => alpha(theme.palette.text.primary, 0.8),
            display: { sm: "none", md: "flex" },
          }}
        >
          Status
        </Typography>
        <Button
          {...(linkToException && {
            component: RouterLink,
            to: linkToException,
            endIcon: <ArrowForwardIcon sx={{ color: "secondary.light" }} />,
          })}
          startIcon={<RemoveCircleOutlineIcon />}
          sx={{
            textTransform: "none",
            ...(!linkToException && {
              "&:hover": {
                background: "transparent",
                pointerEvents: "none",
              },
            }),
            fontSize: { sm: "13px", lg: "14px" },
          }}
        >
          Exception created
        </Button>
      </Stack>
    </Grid>
  );
};
